import { roleplay } from 'services/api/roleplayClient';
import type {
  RoleplayMode, RoleplayPerformanceError, RoleplayPerformanceNetwork,
  RoleplayPerformancePatch, RoleplayPerformancePayload,
} from 'services/api/roleplayClient';

export type RoleplayPerformanceRecorder = {
  update: (patch: RoleplayPerformancePatch) => void;
  finish: (status: 'ok' | 'error' | 'cancelled', errorCode?: RoleplayPerformanceError) => void;
  dispose: () => void;
};

const metricNames = new Set(['speech_stop_to_audio_ms', 'submit_to_audio_ms', 'text_first_visible_ms',
  'controls_ready_ms', 'backend_decision_ms', 'transcription_finalization_ms', 'text_to_audio_ms',
  'directive_to_audio_ms', 'playback_ms', 'microphone_handoff_ms',
  'local_speech_stop_to_provider_ms', 'local_speech_stop_to_audio_ms', 'elevenlabs_connection_ms',
  'elevenlabs_text_buffer_ms', 'elevenlabs_first_audio_ms', 'elevenlabs_audio_to_playback_ms',
  'elevenlabs_max_audio_gap_ms']);
const finite = (value: unknown, max = 600000): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= max;

// Do not accept arbitrary metadata: callers can never upload transcript/error text.
export const startRoleplayPerformance = ({ flowName, sessionId, mode, inputMode }: {
  flowName: string; sessionId: number; mode: RoleplayMode; inputMode: RoleplayMode;
}): RoleplayPerformanceRecorder => {
  let recordId: string;
  try { recordId = crypto.randomUUID(); } catch {
    // Diagnostics must never prevent a turn on older/insecure browser contexts.
    return { update: () => undefined, finish: () => undefined, dispose: () => undefined };
  }
  const release = Array.from(document.scripts).map((script) =>
    script.src.match(/\/main\.([a-zA-Z0-9]{1,64})\.js(?:[?#]|$)/)?.[1]).find(Boolean);
  let state: RoleplayPerformancePayload = { record_id: recordId, sequence: 0,
    mode, input_mode: inputMode, status: 'started', ...(release ? { client_release: release } : {}) };
  let timer: ReturnType<typeof setTimeout> | undefined;
  let terminal = false;
  let disposed = false;
  const send = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    if (disposed || state.sequence >= 20) return;
    state = { ...state, sequence: state.sequence + 1 };
    try { void Promise.resolve(roleplay.recordPerformance(flowName, sessionId, state)).catch(() => undefined); }
    catch { /* Even a synchronous transport failure cannot affect playback. */ }
  };
  const finish: RoleplayPerformanceRecorder['finish'] = (status, errorCode) => {
    if (disposed || terminal) return;
    terminal = true;
    state = { ...state, status, ...(errorCode ? { error_code: errorCode } : {}) };
    send();
  };
  send();
  return {
    update: (patch) => {
      if (disposed || terminal) return;
      const metrics = { ...state.metrics };
      Object.entries(patch.metrics ?? {}).forEach(([key, value]) => {
        if (metricNames.has(key) && finite(value)) Object.assign(metrics, { [key]: value });
      });
      const network = { ...state.network, sample_scope: 'openai_webrtc' as const };
      if (patch.network) {
        (['rtt_ms', 'jitter_ms', 'packet_loss_percent'] as const).forEach((key) => {
          const value = patch.network?.[key];
          if (finite(value, key === 'packet_loss_percent' ? 100 : 600000)) network[key] = value;
        });
      }
      state = { ...state, metrics,
        ...(patch.network ? { network } : {}),
        ...(Number.isInteger(patch.learner_turn_id) && (patch.learner_turn_id ?? 0) > 0
          ? { learner_turn_id: patch.learner_turn_id } : {}),
        ...(Number.isInteger(patch.generation) && (patch.generation ?? 0) > 0
          ? { generation: patch.generation } : {}),
      };
      // Reserve the last upload for the terminal state.
      if (timer === undefined && state.sequence < 19) timer = setTimeout(send, 200);
    },
    finish,
    dispose: () => {
      finish('cancelled', 'interrupted');
      disposed = true;
      if (timer !== undefined) clearTimeout(timer);
    },
  };
};

/** Sampling is bounded, contains no addresses, and never delays media handling. */
export const createRoleplayNetworkSampler = (peer: RTCPeerConnection | null) => {
  let previous: { id: string; received: number; lost: number } | undefined;
  return async (): Promise<RoleplayPerformanceNetwork | undefined> => {
    if (!peer || typeof peer.getStats !== 'function') return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const report = await Promise.race([
        peer.getStats(),
        new Promise<undefined>((resolve) => { timer = setTimeout(() => resolve(undefined), 1000); }),
      ]);
      if (!report) return undefined;
      const values: RTCStats[] = [];
      report.forEach((value) => values.push(value));
      const transport = values.find((value) => value.type === 'transport') as RTCTransportStats | undefined;
      const pair = values.find((value) => value.id === transport?.selectedCandidatePairId) as
        RTCIceCandidatePairStats | undefined;
      const audio = values.find((value) => value.type === 'inbound-rtp'
        && (value as RTCInboundRtpStreamStats).kind === 'audio') as RTCInboundRtpStreamStats | undefined;
      const network: RoleplayPerformanceNetwork = { sample_scope: 'openai_webrtc' };
      if (finite(pair?.currentRoundTripTime)) network.rtt_ms = pair!.currentRoundTripTime! * 1000;
      if (finite(audio?.jitter)) network.jitter_ms = audio!.jitter! * 1000;
      if (audio && finite(audio.packetsReceived, Number.MAX_SAFE_INTEGER)
        && finite(audio.packetsLost, Number.MAX_SAFE_INTEGER)) {
        const received = audio.packetsReceived;
        const lost = audio.packetsLost;
        if (previous?.id === audio.id) {
          const receivedDelta = received - previous.received;
          const lostDelta = lost - previous.lost;
          if (receivedDelta >= 0 && lostDelta >= 0 && receivedDelta + lostDelta > 0) {
            network.packet_loss_percent = lostDelta / (receivedDelta + lostDelta) * 100;
          }
        }
        previous = { id: audio.id, received, lost };
      }
      return network;
    } catch { return undefined; }
    finally { if (timer !== undefined) clearTimeout(timer); }
  };
};
